import { useEffect, useState } from "react";
import {
  BRAND_SETTINGS_EVENT,
  BRAND_SETTINGS_STORAGE_KEY,
  DEFAULT_LOGO_SIZE,
} from "@/constants/brand";

type BrandSettings = {
  logoSize: number;
};

const clampLogoSize = (size: number) => Math.min(120, Math.max(40, size));

function readBrandSettings(): BrandSettings {
  if (typeof window === "undefined") {
    return { logoSize: DEFAULT_LOGO_SIZE };
  }

  try {
    const stored = window.localStorage.getItem(BRAND_SETTINGS_STORAGE_KEY);
    if (!stored) return { logoSize: DEFAULT_LOGO_SIZE };

    const parsed = JSON.parse(stored) as Partial<BrandSettings>;
    return {
      logoSize: clampLogoSize(Number(parsed.logoSize) || DEFAULT_LOGO_SIZE),
    };
  } catch {
    return { logoSize: DEFAULT_LOGO_SIZE };
  }
}

export function saveBrandSettings(settings: BrandSettings) {
  if (typeof window === "undefined") return;

  const nextSettings = {
    logoSize: clampLogoSize(settings.logoSize),
  };

  window.localStorage.setItem(BRAND_SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
  window.dispatchEvent(new CustomEvent(BRAND_SETTINGS_EVENT, { detail: nextSettings }));
}

export function resetBrandSettings() {
  saveBrandSettings({ logoSize: DEFAULT_LOGO_SIZE });
}

export function useBrandSettings() {
  const [settings, setSettings] = useState<BrandSettings>(() => readBrandSettings());

  useEffect(() => {
    const syncSettings = () => setSettings(readBrandSettings());

    window.addEventListener(BRAND_SETTINGS_EVENT, syncSettings);
    window.addEventListener("storage", syncSettings);

    return () => {
      window.removeEventListener(BRAND_SETTINGS_EVENT, syncSettings);
      window.removeEventListener("storage", syncSettings);
    };
  }, []);

  return settings;
}
